import { supabase } from '../utils/supabase';

export interface ExternalItem {
  id: number;
  name: string;
  sku: string;
  barcode: string;
  description: string;
  primary_image?: string;
  bran_name?: string; // Brand name from API
  brand?: string; // Normalized brand field
  price?: number; // Will be extracted from product_outlets
  stock?: number; // Will be extracted from product_outlets
  category?: string;
  image_url?: string; // Will be extracted from additional_images or extra_image_url
  extra_image_url?: string; // From favorite items API
  product_outlets?: Array<{
    outlet_id: number;
    outlet_name: string;
    price_inc_tax: number;
    on_hand_inventory: number;
    available_inventory: number;
  }>;
  additional_images?: Array<{
    primary_pic_name: string;
    "240_thumbnail": string;
    "500_thumbnail": string;
    is_primary: boolean;
  }>;
  product_type?: Array<{
    type_name: string;
  }>;
  created_at?: string;
  updated_at?: string;
}

export interface LocalItem {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  description: string;
  category?: string;
  brand?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  current_stock: number;
  average_cost: number;
  total_value: number;
  latest_unit_price?: number; // Latest selling price from most recent stock movement
  images: Array<{
    id: string;
    image_url: string;
    is_primary: boolean;
    image_order: number;
  }>;
  primary_image?: string;
  // POS stock levels (loaded asynchronously)
  pos_stock_levels?: Array<{
    outlet_id: number;
    outlet_name: string;
    price_inc_tax: number;
    on_hand_inventory: number;
    available_inventory: number;
  }>;
  pos_loading?: boolean; // Track if POS data is being loaded
  pos_loaded?: boolean; // Track if POS data has been loaded
  // Shelf and expiry data
  shelves?: ItemShelf[]; // Array of shelf objects with IDs
  expiries?: ItemExpiry[]; // Array of expiry entries
}

export interface ItemShelf {
  id: string;
  shelf_name: string;
}

export interface ItemExpiry {
  id: string;
  expiry_date: string; // ISO date string
  reminder_days?: number; // Days before expiry to remind
  batch_number?: string;
  quantity?: number;
  notes?: string;
  is_reminded?: boolean;
}

export interface ItemSearchResult {
  items: ExternalItem[];
  totalCount: number;
  hasNext: boolean;
}

export interface ItemService {
  // External API calls (Hike POS) - now with pagination support and outlet preference
  searchByBarcode: (barcode: string, page?: number, limit?: number, preferredOutletName?: string) => Promise<{ data: ItemSearchResult | null; error: any }>;
  searchBySku: (sku: string, page?: number, limit?: number, preferredOutletName?: string) => Promise<{ data: ItemSearchResult | null; error: any }>;
  searchByTerm: (term: string, page?: number, limit?: number, preferredOutletName?: string) => Promise<{ data: ItemSearchResult | null; error: any }>;
  getItemsPaginated: (page: number, limit: number, preferredOutletName?: string) => Promise<{ data: ItemSearchResult | null; error: any }>;

  // Favorite Items API
  getFavoriteItems: (page: number, limit: number) => Promise<{ data: ExternalItem[] | null; error: any }>;

  // Local database operations - now with pagination support for search and stock filtering
  searchLocalItems: (
    searchTerm: string,
    storeId?: string,
    page?: number,
    limit?: number,
    stockFilter?: { enabled: boolean; operator: 'gt' | 'lt' | 'eq'; value: number }
  ) => Promise<{ data: LocalItem[] | null; error: any }>;
  addItemToDatabase: (item: ExternalItem, adminUserId: string, storeId: string, quantity?: number, unitCost?: number, unitPrice?: number) => Promise<{ data: any; error: any }>;
  getLocalItems: (
    storeId?: string,
    page?: number,
    limit?: number,
    stockFilter?: { enabled: boolean; operator: 'gt' | 'lt' | 'eq'; value: number }
  ) => Promise<{ data: LocalItem[] | null; error: any }>;
  checkItemExistsInStore: (sku: string, storeId: string) => Promise<{ data: any; error: any }>;
  getItemMovementHistory: (itemId: string, storeId?: string) => Promise<{ data: any; error: any }>;

  // Simple search method for forms - returns simplified Item format
  searchItems: (searchTerm: string, storeId?: string) => Promise<{ data: any[]; error: any }>;

  // Get POS stock levels for a specific SKU
  getPosStockLevels: (sku: string) => Promise<{ data: ExternalItem | null; error: any }>;

  // Price management
  updateItemPriceFromPOS: (itemId: string, sku: string, storeId: string, userId: string) => Promise<{ data: any; error: any }>;
  fetchAllPosItems: (onProgress?: (stage: string, current: number, total: number, message: string) => void, preferredOutletName?: string) => Promise<{ data: ExternalItem[] | null; error: any }>;
  compareAndSyncPrices: (storeId: string, userId: string, dryRun?: boolean, onProgress?: (stage: string, current: number, total: number, message: string) => void, preferredOutletName?: string) => Promise<{ data: any; error: any }>;

  // Shelf management
  addShelf: (itemId: string, shelfName: string, userId?: string) => Promise<any>;
  removeShelf: (shelfId: string, userId?: string) => Promise<any>;

  // Expiry management
  addExpiry: (itemId: string, expiryData: {
    expiry_date: string;
    reminder_days?: number;
    batch_number?: string;
    quantity?: number;
    notes?: string;
  }, userId?: string) => Promise<any>;
  removeExpiry: (expiryId: string, userId?: string) => Promise<any>;

  // Get items expiring soon
  getItemsExpiringSoon: (days?: number, userId?: string) => Promise<any>;
}

const HIKE_POS_BASE_URL = 'https://order.beautello.mv/curl/api.php';
const FAVORITE_ITEMS_URL = 'https://order.beautello.mv/NewProject/src/api/v1/items/get.php';

// Helper function to normalize external item data from different API sources
const normalizeExternalItem = (item: any, source: 'pos' | 'favorite' = 'pos', preferredOutletName?: string): ExternalItem => {
  if (source === 'favorite') {
    // Handle Favorite Items API format
    return {
      id: item.id,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode,
      description: '', // Avoid showing detailed descriptions as requested
      primary_image: item.primary_image,
      bran_name: undefined,
      brand: undefined, // Favorite items API doesn't have brand info
      price: undefined, // Favorite items API doesn't have pricing
      stock: undefined, // Favorite items API doesn't have stock
      category: undefined, // Favorite items API doesn't have category
      image_url: item.extra_image_url, // Use extra_image_url for favorites
      extra_image_url: item.extra_image_url, // Keep original field too
      product_outlets: undefined,
      additional_images: undefined,
      product_type: undefined,
      created_at: item.created_at,
      updated_at: item.updated_at
    };
  } else {
    // Handle main POS API format
    // Extract price and stock from product_outlets (prefer specified outlet or first available)
    let selectedOutlet = item.product_outlets?.[0]; // Default to first outlet

    if (preferredOutletName && item.product_outlets) {
      // Normalize the preferred outlet name for matching (handle "Skin Culture" vs "Skinculture")
      const normalizedPreferred = preferredOutletName.toLowerCase().replace(/\s+/g, '');

      // Try to find the preferred outlet (case-insensitive, space-insensitive comparison)
      const preferredOutlet = item.product_outlets.find((outlet: any) => {
        const normalizedOutletName = outlet.outlet_name?.toLowerCase().replace(/\s+/g, '');
        return normalizedOutletName === normalizedPreferred;
      });

      if (preferredOutlet) {
        selectedOutlet = preferredOutlet;
        console.log(`✅ Using ${preferredOutletName} price for SKU ${item.sku}: MVR ${selectedOutlet.price_inc_tax}`);
      } else {
        console.log(`⚠️ Outlet "${preferredOutletName}" not found for SKU ${item.sku}, using default (${selectedOutlet?.outlet_name})`);
      }
    }

    // Extract image URL from additional_images
    const primaryImage = item.additional_images?.find((img: any) => img.is_primary);

    // Extract category from product_type
    const category = item.product_type?.[0]?.type_name;

    return {
      id: item.id,
      name: item.name,
      sku: item.sku,
      barcode: item.barcode,
      description: item.description || '', // Include description from POS for sync purposes
      primary_image: item.primary_image,
      bran_name: item.bran_name,
      brand: item.bran_name, // Use bran_name as brand
      price: selectedOutlet?.price_inc_tax,
      stock: selectedOutlet?.available_inventory,
      category: category,
      image_url: primaryImage?.['240_thumbnail'],
      product_outlets: item.product_outlets,
      additional_images: item.additional_images,
      product_type: item.product_type,
      created_at: item.created_date_time,
      updated_at: item.last_modified
    };
  }
};

export const itemService: ItemService = {
  // External API calls to Hike POS
  searchByBarcode: async (barcode: string, page: number = 1, limit: number = 20, preferredOutletName?: string) => {
    try {
      // Use search endpoint with barcode as search term
      const response = await fetch(`${HIKE_POS_BASE_URL}?action=search&term=${encodeURIComponent(barcode)}&page_size=${limit}&page=${page}`);
      const result = await response.json();

      if (result.success && result.data?.items) {
        // Filter results to exact barcode match and normalize
        const exactMatches = result.data.items
          .filter((item: any) => item.barcode === barcode)
          .map((item: any) => normalizeExternalItem(item, 'pos', preferredOutletName));

        // For exact searches, if we get no matches but API has more pages,
        // continue searching on next pages until we find matches or exhaust results
        const hasApiNext = result.data.next !== null;
        const hasExactMatches = exactMatches.length > 0;

        // If no exact matches found but API has more results, keep hasNext true
        // If we found matches, determine hasNext based on whether we got full page of matches
        const hasNext = !hasExactMatches ? hasApiNext : (exactMatches.length >= limit && hasApiNext);

        return {
          data: {
            items: exactMatches,
            totalCount: result.data.totalCount || exactMatches.length,
            hasNext: hasNext
          },
          error: null
        };
      }

      return { data: { items: [], totalCount: 0, hasNext: false }, error: null };
    } catch (error) {
      console.error('Error searching by barcode:', error);
      return { data: null, error };
    }
  },

  searchBySku: async (sku: string, page: number = 1, limit: number = 20, preferredOutletName?: string) => {
    try {
      // Use search endpoint with SKU as search term
      const response = await fetch(`${HIKE_POS_BASE_URL}?action=search&term=${encodeURIComponent(sku)}&page_size=${limit}&page=${page}`);
      const result = await response.json();

      if (result.success && result.data?.items) {
        // Filter results to exact SKU match and normalize
        const exactMatches = result.data.items
          .filter((item: any) => item.sku === sku)
          .map((item: any) => normalizeExternalItem(item, 'pos', preferredOutletName));

        // For exact searches, if we get no matches but API has more pages,
        // continue searching on next pages until we find matches or exhaust results
        const hasApiNext = result.data.next !== null;
        const hasExactMatches = exactMatches.length > 0;

        // If no exact matches found but API has more results, keep hasNext true
        // If we found matches, determine hasNext based on whether we got full page of matches
        const hasNext = !hasExactMatches ? hasApiNext : (exactMatches.length >= limit && hasApiNext);

        return {
          data: {
            items: exactMatches,
            totalCount: result.data.totalCount || exactMatches.length,
            hasNext: hasNext
          },
          error: null
        };
      }

      return { data: { items: [], totalCount: 0, hasNext: false }, error: null };
    } catch (error) {
      console.error('Error searching by SKU:', error);
      return { data: null, error };
    }
  },

  searchByTerm: async (term: string, page: number = 1, limit: number = 20, preferredOutletName?: string) => {
    try {
      const url = `${HIKE_POS_BASE_URL}?action=search&term=${encodeURIComponent(term)}&page_size=${limit}&page=${page}`;
      const response = await fetch(url);
      const result = await response.json();

      if (result.success && result.data?.items) {
        // Normalize all results
        const normalizedItems = result.data.items.map((item: any) => normalizeExternalItem(item, 'pos', preferredOutletName));
        return {
          data: {
            items: normalizedItems,
            totalCount: result.data.totalCount || normalizedItems.length,
            hasNext: result.data.next !== null
          },
          error: null
        };
      }

      return { data: { items: [], totalCount: 0, hasNext: false }, error: null };
    } catch (error) {
      console.error('Error searching by term:', error);
      return { data: null, error };
    }
  },

  getItemsPaginated: async (page: number = 1, limit: number = 20, preferredOutletName?: string) => {
    try {
      // Use action=all endpoint to get all items (better than search with empty term)
      const response = await fetch(`${HIKE_POS_BASE_URL}?action=all&page_size=${limit}&page=${page}`);
      const result = await response.json();

      console.log(`📊 API Response for page ${page}:`, {
        success: result.success,
        hasData: !!result.data,
        itemsCount: result.data?.items?.length || 0
      });

      if (result.success && result.data) {
        // Check if items array exists
        if (!result.data.items || !Array.isArray(result.data.items)) {
          console.warn(`⚠️ No items array in response for page ${page}`);
          return {
            data: {
              items: [],
              totalCount: 0,
              hasNext: false
            },
            error: null
          };
        }

        const normalizedItems = result.data.items.map((item: any) => normalizeExternalItem(item, 'pos', preferredOutletName));
        return {
          data: {
            items: normalizedItems,
            totalCount: result.data.totalCount || normalizedItems.length,
            hasNext: result.data.next !== null && result.data.next !== undefined
          },
          error: null
        };
      }

      console.error(`❌ API returned unsuccessful response for page ${page}:`, result);
      return { data: null, error: result.message || 'No data returned' };
    } catch (error) {
      console.error('Error getting paginated items:', error);
      return { data: null, error };
    }
  },

  // Favorite Items API
  getFavoriteItems: async (page: number = 1, limit: number = 20) => {
    try {
      const response = await fetch(`${FAVORITE_ITEMS_URL}?page=${page}&limit=${limit}`);
      const result = await response.json();
      
      if (result.status === 'success' && result.items) {
        // Normalize favorite items with 'favorite' source to handle different structure
        const normalizedItems = result.items.map((item: any) => normalizeExternalItem(item, 'favorite'));
        return { data: normalizedItems, error: null };
      }
      
      return { data: [], error: null };
    } catch (error) {
      console.error('Error getting favorite items:', error);
      return { data: null, error };
    }
  },

  // Local database operations via Supabase RPC
  searchLocalItems: async (
    searchTerm: string, 
    storeId?: string, 
    page: number = 1, 
    limit: number = 20,
    stockFilter?: { enabled: boolean; operator: 'gt' | 'lt' | 'eq'; value: number }
  ) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: [], error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('get_items_with_images_and_stock', {
        p_store_id: storeId || null,
        p_search_term: searchTerm,
        p_page: page,
        p_limit: limit,
        p_stock_filter_enabled: stockFilter?.enabled || false,
        p_stock_operator: stockFilter?.operator || 'gt',
        p_stock_value: stockFilter?.value || 0
      });

      if (error) {
        console.error('Error searching items with images and stock:', error);
        return { data: null, error };
      }

      // Check if RPC returned success
      if (data?.success) {
        return { data: data.data || [], error: null };
      } else {
        console.error('RPC returned error:', data?.error || data?.message);
        return { data: null, error: data?.error || data?.message || 'Unknown error' };
      }
    } catch (error) {
      console.error('Error calling get_items_with_images_and_stock RPC:', error);
      return { data: null, error };
    }
  },

  addItemToDatabase: async (
    item: ExternalItem, 
    adminUserId: string, 
    storeId: string,
    quantity: number = 1,
    unitCost: number = 0,
    unitPrice: number = 0
  ) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: null, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('add_item_with_stock_entry', {
        p_user_id: adminUserId,
        p_store_id: storeId,
        p_item_name: item.name,
        p_sku: item.sku,
        p_barcode: item.barcode || null,
        p_description: item.description || '',
        p_category: item.category || null,
        p_brand: item.brand || null,
        p_image_url: item.image_url || item.extra_image_url || null,
        p_quantity: quantity,
        p_unit_cost: unitCost,
        p_unit_price: unitPrice,
        p_remarks: `Added from POS system - Quantity: ${quantity}`
      });

      if (error) {
        console.error('Error adding item with stock entry:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error calling add_item_with_stock_entry RPC:', error);
      return { data: null, error };
    }
  },

  getLocalItems: async (
    storeId?: string, 
    page: number = 1, 
    limit: number = 20,
    stockFilter?: { enabled: boolean; operator: 'gt' | 'lt' | 'eq'; value: number }
  ) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: [], error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('get_items_with_images_and_stock', {
        p_store_id: storeId || null,
        p_search_term: null,
        p_page: page,
        p_limit: limit,
        p_stock_filter_enabled: stockFilter?.enabled || false,
        p_stock_operator: stockFilter?.operator || 'gt',
        p_stock_value: stockFilter?.value || 0
      });

      if (error) {
        console.error('Error fetching items with images and stock:', error);
        return { data: null, error };
      }

      // Check if RPC returned success
      if (data?.success) {
        return { data: data.data || [], error: null };
      } else {
        console.error('RPC returned error:', data?.error || data?.message);
        return { data: null, error: data?.error || data?.message || 'Unknown error' };
      }
    } catch (error) {
      console.error('Error calling get_items_with_images_and_stock RPC:', error);
      return { data: null, error };
    }
  },

  checkItemExistsInStore: async (sku: string, storeId: string) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: null, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('check_item_exists_in_store', {
        p_sku: sku,
        p_store_id: storeId
      });

      if (error) {
        console.error('Error checking item exists in store:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error calling check_item_exists_in_store RPC:', error);
      return { data: null, error };
    }
  },

  getItemMovementHistory: async (itemId: string, storeId?: string) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: null, error: 'Supabase not configured' };
    }

    try {
      const { data, error } = await supabase.rpc('get_item_transaction_history', {
        p_item_id: itemId,
        p_store_id: storeId || null,
        p_limit: 100,
        p_offset: 0
      });

      if (error) {
        console.error('Error fetching item transaction history:', error);
        return { data: null, error };
      }

      // Check if RPC returned success
      if (data?.success) {
        return { data: data.data?.transactions || [], error: null };
      } else {
        console.error('RPC returned error:', data?.error || data?.message);
        return { data: null, error: data?.error || data?.message || 'Unknown error' };
      }
    } catch (error) {
      console.error('Error calling get_item_transaction_history RPC:', error);
      return { data: null, error };
    }
  },

  // Simple search method for forms - searches both local items and external items
  searchItems: async (searchTerm: string, storeId?: string) => {
    try {
      // First try local items
      const localResult = await itemService.searchLocalItems(searchTerm, storeId, 1, 10);
      if (localResult.data && localResult.data.length > 0) {
        // Convert LocalItem to simple Item format
        const items = localResult.data.map((item: LocalItem) => ({
          id: item.id,
          name: item.name,
          sku: item.sku,
          barcode: item.barcode,
          description: item.description,
          category: item.category,
          brand: item.brand,
          is_active: item.is_active,
          created_at: item.created_at,
          updated_at: item.updated_at
        }));
        return { data: items, error: null };
      }

      // If no local items found, try external search
      const externalResult = await itemService.searchByTerm(searchTerm, 1, 10);
      if (externalResult.data?.items && externalResult.data.items.length > 0) {
        // Convert ExternalItem to simple Item format
        const items = externalResult.data.items.map((item: ExternalItem) => ({
          id: item.id.toString(), // Convert to string for consistency
          name: item.name,
          sku: item.sku,
          barcode: item.barcode,
          description: item.description,
          category: item.category,
          brand: item.brand,
          is_active: true, // External items are assumed active
          created_at: item.created_at || new Date().toISOString(),
          updated_at: item.updated_at || new Date().toISOString()
        }));
        return { data: items, error: null };
      }

      return { data: [], error: null };
    } catch (error) {
      console.error('Error searching items:', error);
      return { data: [], error: error };
    }
  },

  // Get POS stock levels for a specific SKU (lightweight call)
  getPosStockLevels: async (sku: string) => {
    try {
      // Use OLD Hike POS API with SKU search
      const url = `${HIKE_POS_BASE_URL}?action=search&term=${encodeURIComponent(sku)}&page_size=5&page=1`;
      console.log(`🔍 Fetching price for SKU ${sku} from: ${HIKE_POS_BASE_URL}`);

      const response = await fetch(url);

      if (!response.ok) {
        console.error(`❌ HTTP error for SKU ${sku}: ${response.status} ${response.statusText}`);
        return { data: null, error: `HTTP ${response.status}` };
      }

      const result = await response.json();

      if (result.success && result.data?.items) {
        // Find exact SKU match
        const exactMatch = result.data.items.find((item: any) => item.sku === sku);

        if (exactMatch) {
          // Return only the stock and pricing info we need
          const normalizedItem = normalizeExternalItem(exactMatch, 'pos');
          console.log(`✅ Found price for SKU ${sku}: MVR ${normalizedItem.price}`);
          return { data: normalizedItem, error: null };
        } else {
          console.warn(`⚠️ No exact match for SKU ${sku} in API response`);
        }
      }

      return { data: null, error: null };
    } catch (error) {
      console.error(`❌ Error fetching POS stock for SKU ${sku}:`, error);
      return { data: null, error: String(error) };
    }
  },

  // Shelf management
  addShelf: async (itemId: string, shelfName: string, userId?: string) => {
    try {
      const { data, error } = await supabase.rpc('add_item_shelf', {
        p_item_id: itemId,
        p_shelf_name: shelfName,
        p_user_id: userId
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding shelf:', error);
      throw error;
    }
  },

  removeShelf: async (shelfId: string, userId?: string) => {
    try {
      const { data, error } = await supabase.rpc('remove_item_shelf', {
        p_shelf_id: shelfId,
        p_user_id: userId
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error removing shelf:', error);
      throw error;
    }
  },

  // Expiry management  
  addExpiry: async (itemId: string, expiryData: {
    expiry_date: string;
    reminder_days?: number;
    batch_number?: string;
    quantity?: number;
    notes?: string;
  }, userId?: string) => {
    try {
      const { data, error } = await supabase.rpc('add_item_expiry', {
        p_item_id: itemId,
        p_expiry_date: expiryData.expiry_date,
        p_reminder_days: expiryData.reminder_days,
        p_batch_number: expiryData.batch_number,
        p_quantity: expiryData.quantity,
        p_notes: expiryData.notes,
        p_user_id: userId
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding expiry:', error);
      throw error;
    }
  },

  removeExpiry: async (expiryId: string, userId?: string) => {
    try {
      const { data, error } = await supabase.rpc('remove_item_expiry', {
        p_expiry_id: expiryId,
        p_user_id: userId
      });
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error removing expiry:', error);
      throw error;
    }
  },

  // Get items expiring soon
  getItemsExpiringSoon: async (days: number = 30, userId?: string) => {
    try {
      const { data, error } = await supabase.rpc('get_items_expiring_soon', {
        p_days: days,
        p_user_id: userId
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching expiring items:', error);
      throw error;
    }
  },

  // Update item price from POS API
  updateItemPriceFromPOS: async (itemId: string, sku: string, storeId: string, userId: string) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: null, error: 'Supabase not configured' };
    }

    try {
      // First, fetch the latest price from POS API
      const posResult = await itemService.getPosStockLevels(sku);

      if (posResult.error || !posResult.data) {
        return {
          data: null,
          error: 'Failed to fetch price from POS API: ' + (posResult.error || 'No data returned')
        };
      }

      // Extract price from POS data based on store
      // Try to find matching outlet or use first available
      const outlets = posResult.data.product_outlets;
      if (!outlets || outlets.length === 0) {
        return {
          data: null,
          error: 'No outlet pricing information available in POS'
        };
      }

      // Get the price - prefer the first outlet's price
      const newPrice = outlets[0].price_inc_tax;

      if (!newPrice || newPrice <= 0) {
        return {
          data: null,
          error: 'Invalid price from POS API: ' + newPrice
        };
      }

      // Update the price in Supabase using the RPC function
      const { data, error } = await supabase.rpc('update_item_price', {
        p_item_id: itemId,
        p_store_id: storeId,
        p_new_unit_price: newPrice,
        p_user_id: userId,
        p_remarks: `Price synced from POS API - Outlets: ${outlets.map(o => `${o.outlet_name}: MVR ${o.price_inc_tax}`).join(', ')}`
      });

      if (error) {
        console.error('Error updating item price:', error);
        return { data: null, error };
      }

      // Check if RPC returned success
      if (data?.success) {
        return { data: data, error: null };
      } else {
        console.error('RPC returned error:', data?.error || data?.message);
        return { data: null, error: data?.error || data?.message || 'Unknown error' };
      }
    } catch (error) {
      console.error('Error calling update_item_price:', error);
      return { data: null, error };
    }
  },

  // Fetch all items from POS API with PARALLEL pagination using action=all
  fetchAllPosItems: async (
    onProgress?: (stage: string, current: number, total: number, message: string) => void,
    preferredOutletName?: string
  ) => {
    try {
      console.log('🚀 Starting PARALLEL POS catalog fetch...');
      console.log(`📡 API: ${HIKE_POS_BASE_URL}?action=all`);
      if (preferredOutletName) {
        console.log(`🎯 Using preferred outlet: ${preferredOutletName}`);
      }

      const pageSize = 100;
      const BATCH_SIZE = 5; // Fetch 5 pages in parallel
      const estimatedTotalPages = 13;
      let allItems: ExternalItem[] = [];

      // Fetch first page to verify data exists
      if (onProgress) {
        onProgress('Fetching POS items...', 0, estimatedTotalPages, 'Starting...');
      }

      const firstPage = await itemService.getItemsPaginated(1, pageSize, preferredOutletName);
      if (firstPage.error || !firstPage.data) {
        return { data: null, error: `First page failed: ${firstPage.error}` };
      }

      allItems = firstPage.data.items || [];
      console.log(`✅ Page 1: ${allItems.length} items`);

      if (!firstPage.data.hasNext) {
        return { data: allItems, error: null };
      }

      // Fetch remaining pages in parallel batches
      let currentBatch = 2;
      while (currentBatch <= estimatedTotalPages) {
        const pageNumbers = [];
        for (let i = 0; i < BATCH_SIZE && (currentBatch + i) <= estimatedTotalPages; i++) {
          pageNumbers.push(currentBatch + i);
        }

        console.log(`📦 Fetching pages ${pageNumbers[0]}-${pageNumbers[pageNumbers.length - 1]} in parallel...`);

        if (onProgress) {
          onProgress(
            'Fetching POS items...',
            pageNumbers[0],
            estimatedTotalPages,
            `Loading batch ${Math.ceil(currentBatch / BATCH_SIZE)}...`
          );
        }

        // Fetch all pages in batch simultaneously
        const results = await Promise.all(
          pageNumbers.map(page => itemService.getItemsPaginated(page, pageSize, preferredOutletName))
        );

        // Process results
        let hasData = false;
        for (let i = 0; i < results.length; i++) {
          const result = results[i];
          const pageNum = pageNumbers[i];

          if (result.data && result.data.items && result.data.items.length > 0) {
            allItems = allItems.concat(result.data.items);
            hasData = true;
            console.log(`✅ Page ${pageNum}: ${result.data.items.length} items (Total: ${allItems.length})`);
          } else {
            console.log(`⚠️ Page ${pageNum}: No more data`);
          }
        }

        // Stop if last batch had no data
        if (!hasData) break;

        currentBatch += BATCH_SIZE;
      }

      console.log(`✅ Parallel fetch complete: ${allItems.length} total items`);
      return { data: allItems, error: null };

    } catch (error) {
      console.error('❌ Error fetching all items:', error);
      return { data: null, error };
    }
  },

  // Compare Stock Room items with POS and sync prices
  compareAndSyncPrices: async (
    storeId: string,
    userId: string,
    dryRun: boolean = false,
    onProgress?: (stage: string, current: number, total: number, message: string) => void,
    preferredOutletName?: string
  ) => {
    if (!supabase) {
      console.warn('Supabase not configured');
      return { data: null, error: 'Supabase not configured' };
    }

    try {
      console.log('🔄 Starting bulk price sync...');
      if (preferredOutletName) {
        console.log(`🎯 Syncing prices for outlet: ${preferredOutletName}`);
      }

      // Step 1: Fetch ALL items from POS API using action=all (paginated)
      console.log('📦 Step 1: Fetching complete POS catalog...');
      if (onProgress) {
        onProgress('Fetching POS items...', 0, 0, 'Starting to fetch items from POS system...');
      }
      const posResult = await itemService.fetchAllPosItems(onProgress, preferredOutletName);

      if (posResult.error || !posResult.data) {
        return {
          data: null,
          error: 'Failed to fetch POS items: ' + posResult.error
        };
      }

      const posItems = posResult.data;
      console.log(`✅ Fetched ${posItems.length} items from POS`);

      // Create SKU → Price, Description, Barcode, Name, Stock & Image URL map from POS items
      const posDataMap = new Map<string, { price: number; description: string; barcode: string; name: string; stock: number; image_url: string | null }>();
      posItems.forEach(item => {
        if (item.sku && item.price && item.price > 0) {
          posDataMap.set(item.sku, {
            price: item.price,
            description: item.description || '',
            barcode: item.barcode || '',
            name: item.name || '',
            stock: item.stock || 0,  // Available inventory from POS
            image_url: item.image_url || item.extra_image_url || null  // Primary image URL from POS
          });
        }
      });
      console.log(`✅ Created data map with ${posDataMap.size} items`);

      // Step 2: Fetch all Stock Room items for this store
      console.log('🏪 Step 2: Fetching Stock Room items...');
      if (onProgress) {
        onProgress('Fetching Stock Room items...', 0, 0, 'Loading items from database...');
      }
      const stockResult = await itemService.getLocalItems(storeId, 1, 100000); // Get all items (increased limit for large catalogs)

      if (stockResult.error || !stockResult.data) {
        return {
          data: null,
          error: 'Failed to fetch Stock Room items: ' + stockResult.error
        };
      }

      const stockItems = stockResult.data;
      console.log(`✅ Fetched ${stockItems.length} items from Stock Room`);

      // Step 3: Compare and build price updates array
      console.log('🔍 Step 3: Comparing prices...');
      if (onProgress) {
        onProgress('Comparing prices...', 0, stockItems.length, 'Analyzing price differences...');
      }
      const priceUpdates: Array<{
        item_id: string;
        sku: string;
        item_name: string;
        old_price: number | null;
        new_price: number;
        old_description: string | null;
        new_description: string;
        old_barcode: string | null;
        new_barcode: string;
        old_name: string | null;
        new_name: string;
        old_image_url: string | null;
        new_image_url: string | null;
      }> = [];

      let matched = 0;
      let priceDifferences = 0;

      stockItems.forEach(stockItem => {
        if (!stockItem.sku) return;

        const posData = posDataMap.get(stockItem.sku);
        if (posData) {
          matched++;

          const currentPrice = stockItem.latest_unit_price || null;
          const currentDescription = stockItem.description || '';
          const currentBarcode = stockItem.barcode || '';
          const currentName = stockItem.name || '';
          const currentImageUrl = stockItem.primary_image || null;
          const priceDiff = currentPrice ? Math.abs(currentPrice - posData.price) : null;
          const descriptionDiff = currentDescription !== posData.description;
          const barcodeDiff = currentBarcode !== posData.barcode;
          const nameDiff = currentName !== posData.name;
          const imageUrlDiff = currentImageUrl !== posData.image_url;

          // If prices differ by more than 0.01 OR description differs OR barcode differs OR name differs OR image URL differs, add to updates
          if (!currentPrice || (priceDiff && priceDiff >= 0.01) || descriptionDiff || barcodeDiff || nameDiff || imageUrlDiff) {
            priceDifferences++;
            priceUpdates.push({
              item_id: stockItem.id,
              sku: stockItem.sku,
              item_name: stockItem.name || '',
              old_price: currentPrice,
              new_price: posData.price,
              old_description: currentDescription || null,
              new_description: posData.description,
              old_barcode: currentBarcode || null,
              new_barcode: posData.barcode,
              old_name: currentName || null,
              new_name: posData.name,
              old_image_url: currentImageUrl,
              new_image_url: posData.image_url
            });
          }
        }
      });

      console.log(`✅ Comparison complete:`);
      console.log(`   - Matched by SKU: ${matched}`);
      console.log(`   - Price/Description/Barcode differences: ${priceDifferences}`);

      if (onProgress) {
        onProgress('Comparison complete', matched, stockItems.length, `Found ${priceDifferences} price differences`);
      }

      // Step 3.5: Create stock snapshot for comparison tracking
      console.log('📊 Step 3.5: Creating stock snapshot...');
      const stockSnapshot: Array<{
        item_id: string;
        sku: string;
        item_name: string;
        pos_stock: number;
        pos_price: number;
        stock_room_stock: number;
        stock_room_price: number;
      }> = [];

      let stockDifferences = 0;

      stockItems.forEach(stockItem => {
        if (!stockItem.sku) return;

        const posData = posDataMap.get(stockItem.sku);
        if (posData) {
          const posStock = posData.stock || 0;
          const roomStock = stockItem.current_stock || 0;
          const stockDiff = posStock - roomStock;

          stockSnapshot.push({
            item_id: stockItem.id,
            sku: stockItem.sku,
            item_name: stockItem.name || '',
            pos_stock: posStock,
            pos_price: posData.price,
            stock_room_stock: roomStock,
            stock_room_price: stockItem.latest_unit_price || 0
          });

          // Count items with stock differences
          if (stockDiff !== 0) {
            stockDifferences++;
          }
        }
      });

      console.log(`📊 Stock comparison:`);
      console.log(`   - Items with stock differences: ${stockDifferences}`);

      // Save stock snapshot to database (don't wait for it)
      if (!dryRun && stockSnapshot.length > 0) {
        supabase.rpc('sync_pos_stock_snapshot', {
          p_store_id: storeId,
          p_stock_data: stockSnapshot,
          p_user_id: userId
        }).then(result => {
          if (result.error) {
            console.error('⚠️ Failed to save stock snapshot:', result.error);
          } else {
            console.log(`✅ Stock snapshot saved: ${stockSnapshot.length} items`);
          }
        });
      }

      // If dry run, return preview without updating
      if (dryRun) {
        return {
          data: {
            success: true,
            dry_run: true,
            total_pos_items: posItems.length,
            total_stockroom_items: stockItems.length,
            matched_by_sku: matched,
            price_differences: priceDifferences,
            preview: priceUpdates,
            stock_snapshot: stockSnapshot,  // Include stock data in preview
            message: `Preview: ${priceDifferences} prices would be updated`
          },
          error: null
        };
      }

      // Step 4: If no updates needed, return early
      if (priceUpdates.length === 0) {
        return {
          data: {
            success: true,
            total_pos_items: posItems.length,
            total_stockroom_items: stockItems.length,
            matched_by_sku: matched,
            price_differences: 0,
            updated: 0,
            skipped: 0,
            errors: [],
            changes: [],
            message: 'All prices are already up to date!'
          },
          error: null
        };
      }

      // Step 5: Apply bulk updates via RPC
      console.log(`💾 Step 4: Applying ${priceUpdates.length} price updates...`);
      if (onProgress) {
        onProgress('Updating prices...', 0, priceUpdates.length, `Applying ${priceUpdates.length} price changes...`);
      }

      const { data, error } = await supabase.rpc('bulk_sync_prices_from_pos', {
        p_store_id: storeId,
        p_price_updates: priceUpdates,
        p_user_id: userId
      });

      if (error) {
        console.error('❌ Error calling bulk_sync_prices_from_pos:', error);
        return { data: null, error };
      }

      // Enhance result with metadata
      const result = {
        ...data,
        total_pos_items: posItems.length,
        total_stockroom_items: stockItems.length,
        matched_by_sku: matched,
        price_differences: priceDifferences
      };

      console.log('✅ Bulk price sync complete:', result);
      return { data: result, error: null };

    } catch (error) {
      console.error('❌ Error in compareAndSyncPrices:', error);
      return { data: null, error };
    }
  }
};